import { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import BirddogLayout from "../components/BirddogLayout"
import BirddogPrimaryButton from "../components/BirddogPrimaryButton"
import { fetchSales, publishSale } from "../services/salesApi"
import { Sale } from "../types"
import {
  getSaleStatusBadgeStyles,
  getBadgeBaseClasses,
} from "../utils/badgeStyles"
import timberBackground from "../images/eligibility-quiz-timber.webp"
import cardImage from "../images/empty-card-circle.svg"

export const ForesterDashboard = () => {
  const navigate = useNavigate()
  const [saleTypeFilter, setSaleTypeFilter] = useState<string>("all")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [sales, setSales] = useState<Sale[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>("")
  const [publishingId, setPublishingId] = useState<number | null>(null)
  const [publishError, setPublishError] = useState<string>("")
  const [showDraftNotification, setShowDraftNotification] = useState(true)

  // Fetch sales data on component mount
  useEffect(() => {
    loadSales()
  }, [])

  const loadSales = async () => {
    try {
      setLoading(true)
      setError("")
      const salesData = await fetchSales()
      setSales(salesData)
    } catch (err) {
      console.error("Failed to fetch sales:", err)
      setError(err instanceof Error ? err.message : "Failed to load sales")
    } finally {
      setLoading(false)
    }
  }

  const filteredSales = sales.filter((sale) => {
    const matchesType =
      saleTypeFilter === "all" || sale.sale_type === saleTypeFilter
    const matchesStatus = statusFilter === "all" || sale.status === statusFilter
    return matchesType && matchesStatus
  })

  const handleSaleClick = (sale: Sale) => {
    if (sale.status === "active" || sale.status === "closed") {
      navigate(`/birddog/sale/${sale.id}`)
    }
  }

  const handleRetry = () => {
    loadSales()
  }

  const handlePublish = async (sale: Sale) => {
    if (
      !confirm(
        `Are you sure you want to publish "${sale.name}"? Once published, the sale will be active and bidding will begin according to the scheduled dates.`
      )
    ) {
      return
    }

    try {
      setPublishingId(sale.id)
      setPublishError("")
      await publishSale(sale.id)

      // Reload the sales list to show updated status
      await loadSales()
    } catch (err) {
      console.error("Failed to publish sale:", err)
      setPublishError(
        err instanceof Error ? err.message : "Failed to publish sale"
      )
    } finally {
      setPublishingId(null)
    }
  }

  // Find the first draft sale for the CTA
  const draftSale = sales.find((s) => s.status === "draft")

  return (
    <BirddogLayout>
      <div>
        {/* CTA Boxes */}
        {draftSale && showDraftNotification && (
          <div className="bg-white flex border-l-2 border-leaf rounded-r-sm shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] p-4 gap-4 mb-6">
            <figure className="w-[100px] flex items-center justify-center shrink-0">
              <img src={cardImage} alt="Draft sale" />
            </figure>
            <div className="grow">
              <div className="flex justify-between mb-2">
                <h3 className="text-charcoal-500 text-base font-bold leading-[150%] tracking-[0.32px]">
                  Finish your draft timber sale
                </h3>
                <button
                  onClick={() => setShowDraftNotification(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <p className="text-gray-600 text-sm mb-3">
                You have a draft sale "{draftSale.name}" that hasn't been
                published yet. Complete and publish it to start accepting bids.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <BirddogPrimaryButton
                  onClick={() =>
                    void navigate(`/birddog/sale/${draftSale.id}/edit`)
                  }
                >
                  Jump back in
                </BirddogPrimaryButton>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-sm shadow-sm">
          <div className="p-4 md:px-12 md:py-6">
            <div className="flex items-center justify-between mb-6">
              <h2
                className="font-medium text-sm text-stone-600 uppercase tracking-wider"
                style={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  letterSpacing: "0.06em",
                }}
              >
                Timber Sales
              </h2>
              <div className="flex items-end space-x-4">
                <BirddogPrimaryButton
                  onClick={() => void navigate("/birddog/sale/create")}
                >
                  Create New Sale
                </BirddogPrimaryButton>
                <div>
                  <label
                    htmlFor="sale-type-filter"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Sale Type
                  </label>
                  <select
                    id="sale-type-filter"
                    value={saleTypeFilter}
                    onChange={(e) => setSaleTypeFilter(e.target.value)}
                    className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="all">All Types</option>
                    <option value="pay as cut">Pay as Cut</option>
                    <option value="lump sum">Lump Sum</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="status-filter"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Status
                  </label>
                  <select
                    id="status-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="all">All</option>
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center space-x-3">
                  <svg
                    className="animate-spin h-6 w-6 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span className="text-gray-600">Loading sales...</span>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-center">
                  <svg
                    className="h-6 w-6 text-red-400 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <h3 className="text-sm font-medium text-red-800">
                      Failed to load sales
                    </h3>
                    <p className="text-sm text-red-600 mt-1">{error}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    onClick={handleRetry}
                    className="bg-red-100 hover:bg-red-200 text-red-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            )}

            {/* Publish Error State */}
            {publishError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-center">
                  <svg
                    className="h-5 w-5 text-red-400 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <h3 className="text-sm font-medium text-red-800">
                      Failed to publish sale
                    </h3>
                    <p className="text-sm text-red-600 mt-1 whitespace-pre-wrap">
                      {publishError}
                    </p>
                  </div>
                  <button
                    onClick={() => setPublishError("")}
                    className="ml-auto text-red-400 hover:text-red-600"
                  >
                    <svg
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!loading &&
              !error &&
              filteredSales.length === 0 &&
              sales.length === 0 && (
                <div className="text-center py-12">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No timber sales
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by creating your first timber sale.
                  </p>
                  <div className="mt-6">
                    <BirddogPrimaryButton
                      onClick={() => void navigate("/birddog/sale/create")}
                    >
                      Create New Sale
                    </BirddogPrimaryButton>
                  </div>
                </div>
              )}

            {/* No Filtered Results State */}
            {!loading &&
              !error &&
              filteredSales.length === 0 &&
              sales.length > 0 && (
                <div className="text-center py-8">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No sales match your filters
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Try adjusting your sale type or status filters.
                  </p>
                </div>
              )}

            {/* Sales List */}
            {!loading && !error && filteredSales.length > 0 && (
              <div className="space-y-3">
                {filteredSales.map((sale) => {
                  const isClickable =
                    sale.status === "active" || sale.status === "closed"
                  return (
                    <div
                      key={sale.id}
                      className={`px-6 py-3 rounded-sm bg-[#D3D3D3] bg-cover bg-center bg-no-repeat ${
                        isClickable
                          ? "cursor-pointer hover:opacity-90 transition-opacity"
                          : "cursor-default"
                      }`}
                      style={{
                        backgroundImage: `linear-gradient(0deg, rgba(36, 36, 38, 0.7) 0%, rgba(36, 36, 38, 0.7) 100%), url(${timberBackground})`,
                      }}
                      onClick={() => handleSaleClick(sale)}
                    >
                      <div className="w-full flex justify-between">
                        <h3 className="text-white text-base leading-[140%] tracking-[0.32px]">
                          {sale.name}
                        </h3>
                        <p className="text-white text-base leading-[140%] tracking-[0.32px]">
                          {sale.sale_type}
                        </p>
                      </div>
                      <p className="text-white text-left text-xs leading-[140%] tracking-[0.36px] mt-1">
                        {sale.location_text}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          {sale.status === "active" && sale.published_at && (
                            <p className="text-xs text-green-300">
                              Published {sale.published_at.toLocaleDateString()}
                            </p>
                          )}
                          {sale.status === "active" && (
                            <span className="text-xs text-white/70">
                              {sale.bid_count} bid
                              {sale.bid_count !== 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/birddog/sale/${sale.id}/edit`)
                            }}
                            className="px-2 py-1 text-xs font-medium text-white bg-white/20 rounded hover:bg-white/30 transition-colors"
                          >
                            Edit
                          </button>
                          {sale.status === "draft" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                void handlePublish(sale)
                              }}
                              disabled={publishingId === sale.id}
                              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                publishingId === sale.id
                                  ? "bg-gray-400/20 text-gray-300 cursor-not-allowed"
                                  : "text-white bg-green-600/30 hover:bg-green-600/40"
                              }`}
                            >
                              {publishingId === sale.id
                                ? "Publishing..."
                                : "Publish"}
                            </button>
                          )}
                          <span
                            className={getBadgeBaseClasses()}
                            style={{
                              ...getSaleStatusBadgeStyles(sale.status),
                              border: "1px solid",
                            }}
                          >
                            {sale.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </BirddogLayout>
  )
}
